// src/components/Listagem.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "../supabaseClient";
import "./Listagem.css";
import "./loader.css";
import { FaTrash, FaPaperPlane, FaComment, FaTimes, FaFilePdf, FaFilter, FaSave, FaEdit, FaCheck, FaSync, FaPlus, FaHistory } from "react-icons/fa";
import { FaMagnifyingGlass } from "react-icons/fa6";
import { MdCalendarToday, MdBusiness } from "react-icons/md";
import Check from "./Check";
import Loading from "./Loading";
import BuscaInsumo from "./BuscaInsumo";
import ListagemPdf from "./ListagemPdf";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

// ─── Avatar de iniciais ────────────────────────────────────────────────────────
function AvatarIcon({ nome, variant }) {
  const inicial = (nome || "?").trim().charAt(0).toUpperCase();
  return (
    <div className={`info-bar-avatar${variant ? ` info-bar-avatar--${variant}` : ""}`}>
      {variant === "date" ? <MdCalendarToday size={15} /> : variant === "company" ? <MdBusiness size={16} /> : inicial}
    </div>
  );
}

// ─── Hook: busca histórico de pedidos de um código no projeto ─────────────────
// ─── Hook: busca histórico de pedidos de um código no projeto ─────────────────
function useHistoricoInsumo(codigo, projetoId, notaAtualId) {
  const [historico, setHistorico] = useState([]);
  const [loading, setLoading] = useState(false);
  const [descricaoInsumo, setDescricaoInsumo] = useState("");

  const buscar = useCallback(async () => {
    if (!codigo?.trim() || codigo.toLowerCase() === "criar" || !projetoId) {
      setHistorico([]);
      setDescricaoInsumo("");
      return;
    }
    setLoading(true);
    try {
      // Buscar descrição do insumo
      let descricao = "";
      try {
        const { data: insumoData } = await supabase
          .from("itens")
          .select("descricao")
          .eq("codigo", codigo.trim())
          .maybeSingle();
        if (insumoData?.descricao) {
          descricao = insumoData.descricao;
        }
      } catch (err) {
        console.warn("Erro ao buscar descrição do insumo:", err);
      }
      setDescricaoInsumo(descricao);

      let query = supabase
        .from("planilha_itens")
        .select("quantidade, locacao, criado_em, data_envio, nota_id")
        .eq("codigo", codigo.trim())
        .eq("projeto_id", projetoId)
        .order("criado_em", { ascending: false });

      if (notaAtualId) query = query.neq("nota_id", notaAtualId);

      const { data, error } = await query;
      if (error) throw error;

      // Agrupar por data + quantidade + locacao para remover duplicatas
      const mapa = new Map();
      
      (data || []).forEach((item) => {
        let locacaoArray = [];
        try {
          if (typeof item.locacao === "string") {
            const p = JSON.parse(item.locacao);
            locacaoArray = Array.isArray(p) ? p : item.locacao ? [item.locacao] : [];
          } else if (Array.isArray(item.locacao)) {
            locacaoArray = item.locacao;
          } else if (item.locacao != null) {
            locacaoArray = [String(item.locacao)];
          }
        } catch { locacaoArray = item.locacao ? [String(item.locacao)] : []; }

        const dataRef = item.data_envio || item.criado_em;
        const dataFormatada = dataRef ? new Date(dataRef).toLocaleDateString("pt-BR") : "–";
        const quantidade = item.quantidade ?? "–";
        const locacaoStr = locacaoArray.length > 0 ? locacaoArray.join(", ") : "–";
        
        const chave = `${dataFormatada}|${quantidade}|${locacaoStr}`;
        
        if (!mapa.has(chave)) {
          mapa.set(chave, {
            data: dataFormatada,
            quantidade: quantidade,
            locacao: locacaoStr,
          });
        }
      });

      const parsed = Array.from(mapa.values());
      
      parsed.sort((a, b) => {
        const dataA = a.data.split('/').reverse().join('');
        const dataB = b.data.split('/').reverse().join('');
        return dataB.localeCompare(dataA);
      });

      setHistorico(parsed);
    } catch (err) {
      console.error("Erro ao buscar histórico:", err);
      setHistorico([]);
    } finally {
      setLoading(false);
    }
  }, [codigo, projetoId, notaAtualId]);

  return { historico, loading, buscar, descricaoInsumo };
}

// ─── Popover de histórico ─────────────────────────────────────────────────────
function HistoricoPopover({ codigo, projetoId, notaAtualId, onClose }) {
  const { historico, loading, buscar, descricaoInsumo } = useHistoricoInsumo(codigo, projetoId, notaAtualId);
  const ref = useRef(null);

  useEffect(() => { buscar(); }, [buscar]);

  useEffect(() => {
    const handle = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [onClose]);

  return (
    <div ref={ref} className="historico-popover">
      <div className="historico-popover-header">
        <div className="historico-popover-title">
          <span className="historico-popover-codigo">{codigo}</span>
          {descricaoInsumo && (
            <span className="historico-popover-descricao">— {descricaoInsumo}</span>
          )}
        </div>
        <button onClick={onClose} className="historico-popover-close">
          <FaTimes size={10} />
        </button>
      </div>
      {loading ? (
        <div className="historico-popover-loading">Carregando...</div>
      ) : historico.length === 0 ? (
        <div className="historico-popover-empty">Nenhum pedido anterior encontrado.</div>
      ) : (
        <div className="historico-popover-table-wrapper">
          <table className="historico-popover-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Qtd</th>
                <th>Locação</th>
              </tr>
            </thead>
            <tbody>
              {historico.map((h, i) => (
                <tr key={i}>
                  <td>{h.data}</td>
                  <td>{h.quantidade}</td>
                  <td>
                    <span className="historico-locacao-cell" title={h.locacao}>
                      {h.locacao}
                      <span className="full-list">{h.locacao}</span>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Função para registrar atividades na tabela project_atividades ─────────────────────────
const registrarAtividadeDetalhada = async ({
  projectId,
  userId,
  tipoAcao,
  nomeItem,
  setorNome = null,
  dataReferencia = new Date()
}) => {
  if (!projectId || !userId) {
    console.warn("⚠️ Não foi possível registrar atividade: projectId ou userId ausente");
    return;
  }

  const agora = new Date(dataReferencia);
  const horaFormatada = agora.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const dataFormatada = agora.toLocaleDateString('pt-BR');
  
  let mensagem = "";
  
  let nomeUsuario = "Usuário";
  try {
    const { data: profile } = await supabase
      .from("profiles")
      .select("nome")
      .eq("id", userId)
      .single();
    nomeUsuario = profile?.nome || "Usuário";
  } catch (err) {
    console.error("Erro ao buscar nome do usuário:", err);
  }
  
  switch (tipoAcao) {
    case "criou_lista":
      mensagem = `${nomeUsuario} criou a lista "${nomeItem}" em ${dataFormatada} às ${horaFormatada}`;
      break;
    case "salvou_rascunho":
      mensagem = `${nomeUsuario} salvou a lista "${nomeItem}" em ${dataFormatada} às ${horaFormatada}`;
      break;
    case "editou_lista":
      mensagem = `${nomeUsuario} editou a lista "${nomeItem}" em ${dataFormatada} às ${horaFormatada}`;
      break;
    case "enviou_lista":
      mensagem = `${nomeUsuario} enviou a lista "${nomeItem}" para o setor ${setorNome} em ${dataFormatada} às ${horaFormatada}`;
      break;
    default:
      mensagem = `${nomeUsuario} ${tipoAcao} "${nomeItem}" em ${dataFormatada} às ${horaFormatada}`;
  }

  try {
    const { error } = await supabase
      .from("project_atividades")
      .insert({
        project_id: projectId,
        user_id: userId,
        tipo_acao: tipoAcao,
        entidade: "nota",
        entidade_id: null,
        nome_item: nomeItem,
        meta: { 
          mensagem,
          setor: setorNome,
          data: dataReferencia.toISOString()
        },
        created_at: dataReferencia.toISOString()
      });
    
    if (error) throw error;
    console.log(`✅ Atividade registrada: ${mensagem}`);
  } catch (err) {
    console.error("❌ Erro ao registrar atividade:", err);
  }
};

// ─── Avisos do sistema ─────────────────────────────────────────────────────────
const AVISOS = [
  {
    id: "aviso_rascunho_v1",
    texto: "💾 Novo: Agora você pode salvar um rascunho e continuar preenchendo depois, sem precisar enviar.",
  },
];

const DIAS_SEMANA = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];

function useAvisosFechados() {
  const [fechados, setFechados] = useState(() => {
    try {
      const salvo = localStorage.getItem("listagem_avisos_fechados");
      return salvo ? JSON.parse(salvo) : [];
    } catch {
      return [];
    }
  });

  const fecharAviso = (id) => {
    setFechados((prev) => {
      const novo = [...new Set([...prev, id])];
      localStorage.setItem("listagem_avisos_fechados", JSON.stringify(novo));
      return novo;
    });
  };

  return { fechados, fecharAviso };
}

let _uidCounter = 0;
function gerarUid() {
  return `_local_${++_uidCounter}_${Date.now()}`;
}

function garantirUid(row) {
  if (!row._uid) row._uid = gerarUid();
  return row;
}

export default function Listagem({ projetoAtual, notaAtual, containerAtual, onStatusUpdate, onClose }) {
  const [rows, setRows] = useState([]);
  const [ultimaAlteracao, setUltimaAlteracao] = useState("");
  const [locacoes, setLocacoes] = useState([]);
  const [eaps, setEaps] = useState([]);
  const [unidadesDisponiveis, setUnidadesDisponiveis] = useState([]);
  const [nomeUsuarioLogado, setNomeUsuarioLogado] = useState("Usuário");
  const [codigoErro, setCodigoErro] = useState(new Set());
  const [setoresContainer, setSetoresContainer] = useState([]);
  const [setorSelecionado, setSetorSelecionado] = useState("");
  const [userIdLogado, setUserIdLogado] = useState("");
  const [statusEnvio, setStatusEnvio] = useState(null);
  const [statusSalvar, setStatusSalvar] = useState(null);
  const [statusAtualizar, setStatusAtualizar] = useState(null);
  const [loading, setLoading] = useState(true);

  const [infoGerador, setInfoGerador] = useState(null);
  const [infoEnvio, setInfoEnvio] = useState(null);
  const [infoRespondido, setInfoRespondido] = useState(null);
  const [listagemEnviada, setListagemEnviada] = useState(false);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [buscaInsumoAberta, setBuscaInsumoAberta] = useState(false);
  const [linhaBuscaAtiva, setLinhaBuscaAtiva] = useState(null);

  // ─── Histórico popover ────────────────────────────────────────────────────────
  const [historicoAbertoUid, setHistoricoAbertoUid] = useState(null);

  // ─── Quantidade de novas linhas ───────────────────────────────────────────────
  const [qtdNovasLinhas, setQtdNovasLinhas] = useState("");

  const [dropdownAberto, setDropdownAberto] = useState(null);
  const dropdownRef = useRef(null);

  const [tooltipVisualizacao, setTooltipVisualizacao] = useState(null);
  const tooltipRef = useRef(null);

  const [filtros, setFiltros] = useState({ locacao: "", eap: "" });
  const [filtroAbertoCol, setFiltroAbertoCol] = useState(null);
  const filtroRef = useRef(null);
  const cardRef = useRef(null);
  const notaCarregadaRef = useRef(null);
  const [forcarAtualizacao, setForcarAtualizacao] = useState(0);
  const isDraggingRef = useRef(false);

  const { fechados, fecharAviso } = useAvisosFechados();
  const avisosVisiveis = AVISOS.filter((a) => !fechados.includes(a.id));

  const [avisoProjeto, setAvisoProjeto] = useState([]);
  const [avisosDescartados, setAvisosDescartados] = useState(new Set());

  useEffect(() => {
    if (!projetoAtual?.id) {
      setAvisoProjeto([]);
      setAvisosDescartados(new Set());
      return;
    }
    setAvisosDescartados(new Set());
    supabase
      .from("project_avisos")
      .select("*")
      .eq("project_id", projetoAtual.id)
      .then(({ data }) => {
        if (data) setAvisoProjeto(data);
      });
  }, [projetoAtual?.id]);

  const hoje = new Date().getDay();

  const avisosVisivelsProjeto = avisoProjeto.filter((a) => {
    if (avisosDescartados.has(a.id)) return false;
    if (a.dia_semana === null || a.dia_semana === undefined) return true;
    return hoje !== a.dia_semana;
  });

  const getRascunhoKey = () => `rascunho_listagem_${projetoAtual?.id}_${notaAtual?.id}`;

  useEffect(() => {
    if (!projetoAtual?.id || !notaAtual?.id || loading) return;
    
    const chaveCriacao = `nota_criada_${notaAtual.id}`;
    const jaRegistrou = sessionStorage.getItem(chaveCriacao);
    
    if (!jaRegistrou && notaAtual.created_at && userIdLogado) {
      const dataCriacao = new Date(notaAtual.created_at);
      const agora = new Date();
      const diffHoras = (agora - dataCriacao) / (1000 * 60 * 60);
      
      if (diffHoras < 2) {
        registrarAtividadeDetalhada({
          projectId: projetoAtual.id,
          userId: userIdLogado,
          tipoAcao: "criou_lista",
          nomeItem: notaAtual.nome,
          dataReferencia: dataCriacao
        });
        sessionStorage.setItem(chaveCriacao, "true");
      }
    }
  }, [projetoAtual?.id, notaAtual?.id, loading, userIdLogado]);

  useEffect(() => {
    if (!modoEdicao || !projetoAtual?.id || !notaAtual?.id || listagemEnviada) return;
    
    const chaveEdicao = `editou_lista_${notaAtual.id}`;
    const jaRegistrou = sessionStorage.getItem(chaveEdicao);
    
    if (!jaRegistrou && userIdLogado) {
      registrarAtividadeDetalhada({
        projectId: projetoAtual.id,
        userId: userIdLogado,
        tipoAcao: "editou_lista",
        nomeItem: notaAtual.nome,
        dataReferencia: new Date()
      });
      sessionStorage.setItem(chaveEdicao, "true");
      
      setTimeout(() => {
        sessionStorage.removeItem(chaveEdicao);
      }, 10000);
    }
  }, [modoEdicao, projetoAtual?.id, notaAtual?.id, listagemEnviada, userIdLogado]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownAberto(null);
      }
    };
    if (dropdownAberto !== null) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [dropdownAberto]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target)) {
        setTooltipVisualizacao(null);
      }
    };
    if (tooltipVisualizacao !== null) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [tooltipVisualizacao]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filtroRef.current && !filtroRef.current.contains(e.target)) {
        setFiltroAbertoCol(null);
      }
    };
    if (filtroAbertoCol !== null) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [filtroAbertoCol]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      const { data, error } = await supabase.auth.getUser();
      if (error) return;
      const user = data.user;
      if (user) {
        const { data: profile, error: profileError } = await supabase
          .from("profiles")
          .select("nome, id")
          .eq("id", user.id)
          .single();
        if (profileError) {
          setNomeUsuarioLogado(user.email?.split("@")[0] || "Usuário");
          setUserIdLogado("");
        } else {
          setNomeUsuarioLogado(profile?.nome || user.email?.split("@")[0] || "Usuário");
          setUserIdLogado(profile?.id || "");
        }
      }
    };
    fetchUserProfile();
  }, []);

  useEffect(() => {
    if (!containerAtual?.id) {
      setSetoresContainer([]);
      return;
    }
    const carregarSetores = async () => {
      try {
        const { data, error } = await supabase
          .from("setores")
          .select("id, name, nickname")
          .eq("user_id", containerAtual.id)
          .order("nickname");
        if (error) throw error;
        setSetoresContainer(data || []);
      } catch (err) {
        console.error("Erro ao carregar setores do container:", err);
        setSetoresContainer([]);
      }
    };
    carregarSetores();
  }, [containerAtual?.id]);

  useEffect(() => {
    if (!projetoAtual?.id) {
      setLocacoes([]);
      setEaps([]);
      setUnidadesDisponiveis([]);
      return;
    }
    const carregarReferenciasDoProjeto = async () => {
      try {
        const [pavimentosRes, eapsRes, unidadesRes] = await Promise.all([
          supabase
            .from("pavimentos")
            .select("name")
            .eq("project_id", projetoAtual.id)
            .order("ordem", { ascending: true }),
          supabase
            .from("eap")
            .select("name")
            .eq("project_id", projetoAtual.id)
            .order("ordem", { ascending: true }),
          supabase.from("itens").select("unidade"),
        ]);
        setLocacoes(pavimentosRes.data?.map((p) => p.name) || []);
        setEaps(eapsRes.data?.map((e) => e.name) || []);
        setUnidadesDisponiveis([
          ...new Set(unidadesRes.data?.map((u) => u.unidade).filter(Boolean) || []),
        ]);
      } catch (err) {
        console.error("Erro ao carregar referências do projeto:", err);
        setLocacoes([]);
        setEaps([]);
        setUnidadesDisponiveis([]);
      }
    };
    carregarReferenciasDoProjeto();
  }, [projetoAtual?.id]);

  const registrarAlteracao = (autor = nomeUsuarioLogado) => {
    const agora = new Date();
    setUltimaAlteracao(`${autor} alterou em ${agora.toLocaleDateString()} ${agora.toLocaleTimeString()}`);
  };

  const normalizarRow = (item) => {
    let locacaoArray = [];
    try {
      if (typeof item.locacao === "string") {
        const parsed = JSON.parse(item.locacao);
        locacaoArray = Array.isArray(parsed) ? parsed : item.locacao ? [item.locacao] : [];
      } else if (Array.isArray(item.locacao)) {
        locacaoArray = item.locacao;
      } else if (item.locacao != null) {
        locacaoArray = [String(item.locacao)];
      }
    } catch (e) {
      locacaoArray = item.locacao ? [String(item.locacao)] : [];
    }
    return garantirUid({
      id: item.id,
      codigo: item.codigo || "",
      descricao: item.descricao || "",
      unidade: item.unidade || "",
      quantidade: item.quantidade || "",
      locacao: locacaoArray,
      eap: item.eap || "",
      observacao: item.observacao || "",
      comentario: item.comentario || "",
      criado_em: item.criado_em || null,
      grupo_envio: item.grupo_envio || "antigo",
      data_envio: item.data_envio || item.criado_em,
      enviado_por: item.enviado_por || "Usuário",
      ordem: item.ordem ?? 0,
    });
  };

  const carregarDadosDoBanco = async () => {
    try {
      if (!projetoAtual?.id || !notaAtual?.id) {
        setRows([garantirUid({
          codigo: "", descricao: "", unidade: "", quantidade: "",
          locacao: [], eap: "", observacao: "", comentario: "", ordem: 1,
        })]);
        return;
      }

      const { data: notaData } = await supabase
        .from("notas")
        .select(
          "enviada, data_envio, enviado_por_nome, created_at, created_by, respondida, respondido_por_nome"
        )
        .eq("id", notaAtual.id)
        .single();

      if (notaData) {
        setListagemEnviada(!!notaData.enviada);
        setModoEdicao(false);

        let nomeGerador = null;
        if (notaData.created_by) {
          const { data: perfilCriador } = await supabase
            .from("profiles")
            .select("nome")
            .eq("id", notaData.created_by)
            .single();
          nomeGerador = perfilCriador?.nome || null;
        }

        setInfoGerador(
          nomeGerador ? { nome: nomeGerador, data: notaData.created_at } : null
        );
        setInfoEnvio(
          notaData.enviada && notaData.enviado_por_nome
            ? { nome: notaData.enviado_por_nome, data: notaData.data_envio }
            : null
        );
        setInfoRespondido(
          notaData.respondida && notaData.respondido_por_nome
            ? { nome: notaData.respondido_por_nome }
            : null
        );
      }

      const { data: itensRes, error: itensError } = await supabase
        .from("planilha_itens")
        .select("*")
        .eq("nota_id", notaAtual.id)
        .order("ordem", { ascending: true });

      if (itensError) throw itensError;

      if (itensRes?.length) {
        const mapped = itensRes.map(normalizarRow);
        const comOrdemCorreta = mapped.map((r, i) => ({
          ...r,
          ordem: r.ordem && r.ordem > 0 ? r.ordem : i + 1,
        }));
        setRows(comOrdemCorreta);
      } else {
        setRows([garantirUid({
          codigo: "", descricao: "", unidade: "", quantidade: "",
          locacao: [], eap: "", observacao: "", comentario: "", ordem: 1,
        })]);
      }
      registrarAlteracao();
    } catch (err) {
      console.error("Erro ao carregar dados da nota:", err);
      alert("Erro ao carregar os dados da lista.");
    }
  };

  useEffect(() => {
    if (!projetoAtual?.id || !notaAtual?.id || loading || listagemEnviada) return;
    const key = getRascunhoKey();
    const rascunho = rows.filter(
      (r) => r.codigo?.trim() || r.descricao?.trim() || (r.quantidade && r.quantidade.toString().trim())
    );
    if (rascunho.length > 0) {
      localStorage.setItem(key, JSON.stringify(rascunho));
    } else {
      localStorage.removeItem(key);
    }
  }, [rows, projetoAtual?.id, notaAtual?.id, loading, listagemEnviada]);

  useEffect(() => {
    const carregarRascunhoOuBanco = async () => {
      const chaveAtual = `${projetoAtual?.id}_${notaAtual?.id}`;
      if (notaCarregadaRef.current === chaveAtual && forcarAtualizacao === 0) return;

      setLoading(true);

      if (!projetoAtual?.id || !notaAtual?.id) {
        setRows([garantirUid({
          codigo: "", descricao: "", unidade: "", quantidade: "",
          locacao: [], eap: "", observacao: "", comentario: "", ordem: 1,
        })]);
        setLoading(false);
        return;
      }

      const { data: notaCheck } = await supabase
        .from("notas")
        .select("enviada")
        .eq("id", notaAtual.id)
        .single();

      if (notaCheck?.enviada) {
        await carregarDadosDoBanco();
        notaCarregadaRef.current = chaveAtual;
        setLoading(false);
        return;
      }

      const key = getRascunhoKey();
      const rascunhoSalvo = localStorage.getItem(key);

      if (rascunhoSalvo) {
        try {
          const parsed = JSON.parse(rascunhoSalvo);
          if (Array.isArray(parsed) && parsed.length > 0) {
            const withArrayLocacao = parsed.map((r) => garantirUid({
              ...r,
              locacao: Array.isArray(r.locacao) ? r.locacao : r.locacao ? [r.locacao] : [],
            }));
            setRows(withArrayLocacao);
            registrarAlteracao("Rascunho local");
            notaCarregadaRef.current = chaveAtual;
            setLoading(false);
            return;
          }
        } catch (e) {
          console.warn("Rascunho corrompido, ignorando.", e);
          localStorage.removeItem(key);
        }
      }

      await carregarDadosDoBanco();
      notaCarregadaRef.current = chaveAtual;
      setLoading(false);
    };
    carregarRascunhoOuBanco();
  }, [projetoAtual?.id, notaAtual?.id, forcarAtualizacao]);

  const modoEdicaoRef = useRef(modoEdicao);
  useEffect(() => { modoEdicaoRef.current = modoEdicao; }, [modoEdicao]);

  useEffect(() => {
    if (!notaAtual?.id) return;
    const verificarAtualizacoes = async () => {
      if (modoEdicaoRef.current || isDraggingRef.current) return;

      try {
        const { data, error } = await supabase
          .from("planilha_itens")
          .select(
            "id, codigo, descricao, comentario, observacao, quantidade, unidade, locacao, eap"
          )
          .eq("nota_id", notaAtual.id);
        if (error) throw error;

        setRows((prev) =>
          prev.map((r) => {
            if (!r.id) return r;
            const itemAtualizado = data.find((i) => i.id === r.id);
            if (itemAtualizado) {
              let locacaoArray = [];
              try {
                if (typeof itemAtualizado.locacao === "string") {
                  const parsed = JSON.parse(itemAtualizado.locacao);
                  locacaoArray = Array.isArray(parsed) ? parsed : itemAtualizado.locacao ? [itemAtualizado.locacao] : [];
                } else if (Array.isArray(itemAtualizado.locacao)) {
                  locacaoArray = itemAtualizado.locacao;
                } else if (itemAtualizado.locacao != null) {
                  locacaoArray = [String(itemAtualizado.locacao)];
                }
              } catch (e) {
                locacaoArray = itemAtualizado.locacao ? [String(itemAtualizado.locacao)] : [];
              }
              return {
                ...r,
                codigo: itemAtualizado.codigo,
                descricao: itemAtualizado.descricao,
                comentario: itemAtualizado.comentario,
                observacao: itemAtualizado.observacao,
                quantidade: itemAtualizado.quantidade,
                unidade: itemAtualizado.unidade,
                locacao: locacaoArray,
                eap: itemAtualizado.eap,
              };
            }
            return r;
          })
        );
      } catch (err) {
        console.error("Erro ao verificar atualizações:", err);
      }
    };
    const interval = setInterval(verificarAtualizacoes, 3000);
    return () => clearInterval(interval);
  }, [notaAtual?.id]);

  const findIndexByUid = (uid) => rows.findIndex((r) => r._uid === uid);

  const buscarItemPorCodigo = async (uid, codigo) => {
    const index = findIndexByUid(uid);
    if (index === -1) return;
    if (!codigo?.trim() || codigo.toLowerCase() === "criar") {
      setCodigoErro((prev) => { const novo = new Set(prev); novo.delete(uid); return novo; });
      return;
    }
    try {
      const { data, error } = await supabase
        .from("itens")
        .select("descricao, unidade")
        .eq("codigo", codigo)
        .maybeSingle();
      if (error) throw error;

      setRows((prev) => {
        const novas = [...prev];
        const i = novas.findIndex((r) => r._uid === uid);
        if (i === -1) return prev;
        if (data) {
          novas[i] = { ...novas[i], descricao: data.descricao || "", unidade: data.unidade || "" };
          setCodigoErro((prev2) => { const novo = new Set(prev2); novo.delete(uid); return novo; });
        } else {
          setCodigoErro((prev2) => new Set(prev2).add(uid));
          novas[i] = { ...novas[i], descricao: "", unidade: "" };
        }
        return novas;
      });
      registrarAlteracao();
    } catch (err) {
      setCodigoErro((prev) => new Set(prev).add(uid));
    }
  };

  const handleInputChange = (uid, campo, valor) => {
    setRows((prev) => {
      const novas = [...prev];
      const i = novas.findIndex((r) => r._uid === uid);
      if (i === -1) return prev;
      novas[i] = { ...novas[i], [campo]: valor };
      return novas;
    });
    registrarAlteracao();
  };

  const handleLocacaoChange = (uid, valor, checked) => {
    setRows((prev) => {
      const novas = [...prev];
      const i = novas.findIndex((r) => r._uid === uid);
      if (i === -1) return prev;
      const locacoesAtuais = [...(novas[i].locacao || [])];
      if (checked) {
        if (!locacoesAtuais.includes(valor)) locacoesAtuais.push(valor);
      } else {
        const idx = locacoesAtuais.indexOf(valor);
        if (idx !== -1) locacoesAtuais.splice(idx, 1);
      }
      novas[i] = { ...novas[i], locacao: locacoesAtuais };
      return novas;
    });
    registrarAlteracao();
  };

  const handleObservacaoBlur = (uid, valor) => {
    setRows((prev) => {
      const novas = [...prev];
      const i = novas.findIndex((r) => r._uid === uid);
      if (i === -1) return prev;
      novas[i] = { ...novas[i], observacao: valor };
      return novas;
    });
    registrarAlteracao();
  };

  const handleCodigoEnter = (e, uid, codigo) => {
    if (e.key === "Enter") buscarItemPorCodigo(uid, codigo);
  };

  // ─── Adicionar linha(s) ───────────────────────────────────────────────────────
  const addRow = () => {
    if (listagemEnviada) return;
    const qtd = parseInt(qtdNovasLinhas, 10);
    const quantidade = (!isNaN(qtd) && qtd > 0) ? qtd : 1;

    const ultimaOrdem = rows.length > 0 ? Math.max(...rows.map((r) => r.ordem || 0)) : 0;
    const novasLinhas = Array.from({ length: quantidade }, (_, idx) =>
      garantirUid({
        codigo: "", descricao: "", unidade: "", quantidade: "",
        locacao: [], eap: "", observacao: "", comentario: "",
        criado_em: new Date().toISOString(),
        ordem: ultimaOrdem + idx + 1,
      })
    );
    setRows((prev) => [...prev, ...novasLinhas]);
    registrarAlteracao();
  };

  const removeRow = async (uid) => {
    if (listagemEnviada) return;
    const linha = rows.find((r) => r._uid === uid);
    setRows((prev) => prev.filter((r) => r._uid !== uid));
    registrarAlteracao();
    if (linha?.id) {
      try {
        const { error } = await supabase.from("planilha_itens").delete().eq("id", linha.id);
        if (error) throw error;
      } catch (err) {
        alert("Erro ao excluir item: " + (err.message || "Erro desconhecido"));
        await carregarDadosDoBanco();
      }
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;

    setRows((prev) => {
      const items = [...prev].sort((a, b) => a.ordem - b.ordem);
      const [moved] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, moved);
      const reordered = items.map((item, index) => ({ ...item, ordem: index + 1 }));

      Promise.all(
        reordered
          .filter((item) => item.id)
          .map((item) =>
            supabase.from("planilha_itens").update({ ordem: item.ordem }).eq("id", item.id)
          )
      ).catch((err) => console.error("Erro ao salvar ordem:", err));

      return reordered;
    });
  };

  const handleSetorChange = (e) => {
    setSetorSelecionado(e.target.value);
  };

  const handleExportarPDF = () => {
    ListagemPdf.exportar(
      projetoAtual?.name || "Sem projeto",
      notaAtual?.nome || "Sem nota",
      new Date(),
      rows
    );
  };

  const handleAtualizar = async () => {
    if (!notaAtual?.id) return;
    setStatusAtualizar("atualizando");
    try {
      const { data, error } = await supabase
        .from("planilha_itens")
        .select("*")
        .eq("nota_id", notaAtual.id)
        .order("ordem", { ascending: true });

      if (error) throw error;

      if (data?.length) {
        const mapped = data.map(normalizarRow);
        const comOrdemCorreta = mapped.map((r, i) => ({
          ...r,
          ordem: r.ordem && r.ordem > 0 ? r.ordem : i + 1,
        }));
        setRows(comOrdemCorreta);
      }

      setStatusAtualizar("sucesso");
      setTimeout(() => setStatusAtualizar(null), 2000);
    } catch (err) {
      console.error("Erro ao atualizar:", err);
      setStatusAtualizar(null);
    }
  };

  const handleSalvarRascunho = async () => {
    if (!notaAtual?.id || !projetoAtual?.id) {
      alert("Selecione um projeto e uma nota para salvar.");
      return;
    }

    const { data: { user: currentUser } } = await supabase.auth.getUser();
    const currentUserId = currentUser?.id || userIdLogado;

    setStatusSalvar("salvando");

    try {
      const linhasValidas = rows.filter(
        (r) => r.codigo?.trim() || r.descricao?.trim() || r.quantidade
      );
      const existentes = linhasValidas.filter((r) => r.id);
      const novos = linhasValidas.filter((r) => !r.id);

      if (existentes.length) {
        await Promise.all(
          existentes.map(async (it) => {
            const { error } = await supabase.from("planilha_itens").update({
              codigo: it.codigo?.trim() || null,
              descricao: it.descricao || null,
              unidade: it.unidade || null,
              quantidade: it.quantidade ? Number(it.quantidade) : null,
              locacao: it.locacao && it.locacao.length > 0 ? JSON.stringify(it.locacao) : null,
              eap: it.eap || null,
              observacao: it.observacao || null,
              comentario: it.comentario || null,
              ordem: it.ordem,
            }).eq("id", it.id);
            if (error) throw error;
          })
        );
      }

      if (novos.length) {
        const inserts = novos.map((it) => ({
          projeto_id: projetoAtual.id,
          nota_id: notaAtual.id,
          codigo: it.codigo?.trim() || null,
          descricao: it.descricao || null,
          unidade: it.unidade || null,
          quantidade: it.quantidade ? Number(it.quantidade) : null,
          locacao: it.locacao && it.locacao.length > 0 ? JSON.stringify(it.locacao) : null,
          eap: it.eap || null,
          observacao: it.observacao || null,
          comentario: it.comentario || null,
          grupo_envio: "rascunho",
          criado_em: new Date().toISOString(),
          ordem: it.ordem,
        }));
        const { error } = await supabase.from("planilha_itens").insert(inserts);
        if (error) throw error;
      }

      const key = getRascunhoKey();
      localStorage.setItem(key, JSON.stringify(rows));

      await carregarDadosDoBanco();

      setStatusSalvar("sucesso");
      setTimeout(() => setStatusSalvar(null), 2500);
      registrarAlteracao();

      await registrarAtividadeDetalhada({
        projectId: projetoAtual.id,
        userId: currentUserId,
        tipoAcao: "salvou_rascunho",
        nomeItem: notaAtual.nome,
        dataReferencia: new Date()
      });

    } catch (err) {
      console.error("Erro ao salvar rascunho:", err);
      setStatusSalvar("erro");
      setTimeout(() => setStatusSalvar(null), 3000);
    }
  };

  const handleSave = async () => {
    if (!notaAtual?.id || !projetoAtual?.id || !setorSelecionado) {
      alert("Selecione um setor para enviar a listagem.");
      return;
    }

    const { data: { user: currentUser } } = await supabase.auth.getUser();
    const currentUserId = currentUser?.id || userIdLogado;

    const setorEncontrado = setoresContainer.find(s => s.id === setorSelecionado);
    const nomeSetor = setorEncontrado?.nickname || setorEncontrado?.name || "Setor desconhecido";

    setStatusEnvio("enviando");

    try {
      const grupoEnvio = `envio_${Date.now()}`;
      const dataEnvio = new Date().toISOString();
      const remetente = nomeUsuarioLogado;
      const linhasValidas = rows.filter(
        (r) => r.codigo?.trim() || r.descricao?.trim() || r.quantidade
      );
      const existentes = linhasValidas.filter((r) => r.id);
      const novos = linhasValidas.filter((r) => !r.id);
      const setoresParaEnvio = [setorSelecionado];

      if (existentes.length) {
        await Promise.all(
          existentes.map(async (it) => {
            const { error } = await supabase.from("planilha_itens").update({
              codigo: it.codigo?.trim() || null,
              descricao: it.descricao || null,
              unidade: it.unidade || null,
              quantidade: it.quantidade ? Number(it.quantidade) : null,
              locacao: it.locacao && it.locacao.length > 0 ? JSON.stringify(it.locacao) : null,
              eap: it.eap || null,
              observacao: it.observacao || null,
              comentario: it.comentario || null,
              direcionar_para: JSON.stringify(setoresParaEnvio),
              ordem: it.ordem,
            }).eq("id", it.id);
            if (error) throw error;
          })
        );
      }

      if (novos.length) {
        const inserts = novos.map((it) => ({
          projeto_id: projetoAtual.id,
          nota_id: notaAtual.id,
          codigo: it.codigo?.trim() || null,
          descricao: it.descricao || null,
          unidade: it.unidade || null,
          quantidade: it.quantidade ? Number(it.quantidade) : null,
          locacao: it.locacao && it.locacao.length > 0 ? JSON.stringify(it.locacao) : null,
          eap: it.eap || null,
          observacao: it.observacao || null,
          comentario: it.comentario || null,
          direcionar_para: JSON.stringify(setoresParaEnvio),
          grupo_envio: grupoEnvio,
          data_envio: dataEnvio,
          enviado_por: remetente,
          criado_em: new Date().toISOString(),
          ordem: it.ordem,
        }));
        const { error } = await supabase.from("planilha_itens").insert(inserts);
        if (error) throw error;
      }

      const { data: setorData } = await supabase
        .from("setores")
        .select("user_id")
        .eq("id", setorSelecionado)
        .single();

      const destinatarioId = setorData?.user_id;

      const notificacoesParaInserir = destinatarioId && destinatarioId !== currentUserId
        ? [{
            user_id: destinatarioId,
            remetente_id: currentUserId,
            mensagem: `${remetente} enviou a listagem da nota "${notaAtual.nome}"`,
            projeto_id: projetoAtual.id,
            nota_id: notaAtual.id,
            lido: false,
            created_at: new Date().toISOString(),
            tipo: "listagem_enviada",
          }]
        : [];

      if (notificacoesParaInserir.length > 0) {
        const { error: notifError } = await supabase
          .from("notificacoes")
          .insert(notificacoesParaInserir);
        if (notifError) console.warn("Erro ao enviar notificações:", notifError);
      }

      const setorId = setorSelecionado;
      const { data: pilhasRecebidos, error: pilhasError } = await supabase
        .from("pilhas")
        .select("id")
        .eq("setor_id", setorId)
        .eq("title", "Recebidos")
        .limit(1);

      if (pilhasError) throw pilhasError;

      let pilhaRecebidosId;
      if (!pilhasRecebidos || pilhasRecebidos.length === 0) {
        const { data: novaPilha, error: insertError } = await supabase
          .from("pilhas")
          .insert({ title: "Recebidos", setor_id: setorId, ordem: 0 })
          .select("id")
          .single();
        if (insertError) throw insertError;
        pilhaRecebidosId = novaPilha.id;
      } else {
        pilhaRecebidosId = pilhasRecebidos[0].id;
      }

      const { data: notaExistente } = await supabase
        .from("notas")
        .select("id")
        .eq("nome", notaAtual.nome)
        .eq("pilha_id", pilhaRecebidosId)
        .single();

      let notaEspelhoId;
      if (notaExistente) {
        notaEspelhoId = notaExistente.id;
        await supabase
          .from("notas")
          .update({
            projeto_origem_id: projetoAtual.id,
            nota_original_id: notaAtual.id,
            data_envio: dataEnvio,
            enviado_por_id: currentUserId,
            enviado_por_nome: remetente,
          })
          .eq("id", notaEspelhoId);
      } else {
        const { data: novaNota, error: notaError } = await supabase
          .from("notas")
          .insert({
            nome: notaAtual.nome,
            tipo: "Lista",
            pilha_id: pilhaRecebidosId,
            projeto_origem_id: projetoAtual.id,
            nota_original_id: notaAtual.id,
            enviada: true,
            data_envio: dataEnvio,
            enviado_por_id: currentUserId,
            enviado_por_nome: remetente,
          })
          .select("id")
          .single();
        if (notaError) throw notaError;
        notaEspelhoId = novaNota.id;
      }

      const itensComId = rows.filter((r) => r.id);
      if (itensComId.length) {
        await Promise.all(
          rows
            .filter((r) => r.id)
            .map((r) =>
              supabase.from("planilha_itens").update({ ordem: r.ordem }).eq("id", r.id)
            )
        );
      }

      const { data: itensOriginais, error: itensError } = await supabase
        .from("planilha_itens")
        .select("*")
        .eq("nota_id", notaAtual.id)
        .order("ordem", { ascending: true });

      if (itensError) throw itensError;

      if (itensOriginais?.length > 0) {
        await supabase.from("planilha_itens").delete().eq("nota_id", notaEspelhoId);

        const itensParaInserir = itensOriginais.map((item) => {
          let locacaoArray = [];
          try {
            if (typeof item.locacao === "string") {
              const parsed = JSON.parse(item.locacao);
              locacaoArray = Array.isArray(parsed) ? parsed : item.locacao ? [item.locacao] : [];
            } else if (Array.isArray(item.locacao)) {
              locacaoArray = item.locacao;
            } else if (item.locacao != null) {
              locacaoArray = [String(item.locacao)];
            }
          } catch (e) {
            locacaoArray = item.locacao ? [String(item.locacao)] : [];
          }
          return {
            projeto_id: projetoAtual.id,
            nota_id: notaEspelhoId,
            pilha_id: pilhaRecebidosId,
            codigo: item.codigo,
            descricao: item.descricao,
            unidade: item.unidade,
            quantidade: item.quantidade,
            locacao: locacaoArray.length > 0 ? JSON.stringify(locacaoArray) : null,
            eap: item.eap,
            observacao: item.observacao,
            comentario: item.comentario,
            direcionar_para: item.direcionar_para,
            criado_em: new Date().toISOString(),
            grupo_envio: item.grupo_envio,
            data_envio: item.data_envio,
            enviado_por: item.enviado_por,
            item_original_id: item.id,
            ordem: item.ordem,
          };
        });

        const { error: insertItensError } = await supabase
          .from("planilha_itens")
          .insert(itensParaInserir)
          .select("id, ordem");

        if (insertItensError) throw insertItensError;
      }

      await supabase
        .from("notas")
        .update({
          enviada: true,
          enviado_por_nome: remetente,
          enviado_por_id: currentUserId,
          data_envio: dataEnvio,
        })
        .eq("id", notaAtual.id);

      if (onStatusUpdate) {
        onStatusUpdate(notaAtual.id, { enviada: true, respondida: false });
      }

      setListagemEnviada(true);
      setInfoEnvio({ nome: remetente, data: dataEnvio });
      setCodigoErro(new Set());
      setSetorSelecionado("");
      localStorage.removeItem(getRascunhoKey());
      await carregarDadosDoBanco();
      setStatusEnvio("sucesso");
      setTimeout(() => setStatusEnvio(null), 2000);
      registrarAlteracao();

      await registrarAtividadeDetalhada({
        projectId: projetoAtual.id,
        userId: currentUserId,
        tipoAcao: "enviou_lista",
        nomeItem: notaAtual.nome,
        setorNome: nomeSetor,
        dataReferencia: new Date()
      });

    } catch (err) {
      console.error("Erro ao salvar listagem:", err);
      alert("Erro ao salvar lista.");
      setStatusEnvio(null);
    }
  };

  const formatarData = (isoString) => {
    if (!isoString) return "";
    return new Date(isoString).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  const valoresUnicos = (campo) => {
    const set = new Set();
    rows.forEach((r) => {
      if (campo === "locacao") {
        (r.locacao || []).forEach((l) => l && set.add(l));
      } else if (campo === "eap") {
        if (r.eap) set.add(r.eap);
      } else if (campo === "quantidade") {
        if (r.quantidade !== "" && r.quantidade != null) set.add(String(r.quantidade));
      }
    });
    return [...set].sort();
  };

  const rowsFiltradas = rows.filter((r) => {
    if (filtros.locacao && !(r.locacao || []).includes(filtros.locacao)) return false;
    if (filtros.eap && r.eap !== filtros.eap) return false;
    return true;
  });

  const temFiltroAtivo = filtros.locacao || filtros.eap;

  if (loading) {
    return (
      <div className="listagem-card" ref={cardRef}>
        <Loading />
      </div>
    );
  }

  const rowsParaExibir = [...rowsFiltradas].sort((a, b) => a.ordem - b.ordem);

  return (
    <div className="listagem-card" ref={cardRef}>

      {/* ══ HEADER ══════════════════════════════════════════════════════════════ */}
      <div className="listagem-header-container">
        <div className="listagem-header-titles">
          <span className="project-name">{projetoAtual?.name || "Sem projeto"}</span>
          <div className="sub-info">
            <span className="nota-name">{notaAtual?.nome || "Sem nota"}</span>
          </div>
        </div>
        {onClose && (
          <button className="listagem-close-btn" onClick={onClose} aria-label="Fechar">
            <FaTimes />
          </button>
        )}
      </div>

      {/* ══ AVISOS DO SISTEMA ═══════════════════════════════════════════════════ */}
      {avisosVisiveis.length > 0 && (
        <div className="listagem-avisos-container">
          {avisosVisiveis.map((aviso) => (
            <div key={aviso.id} className="listagem-aviso-bar">
              <span className="listagem-aviso-texto">{aviso.texto}</span>
              <button
                className="listagem-aviso-fechar"
                onClick={() => fecharAviso(aviso.id)}
                aria-label="Fechar aviso"
                title="Fechar aviso (não aparecerá novamente)"
              >
                <FaTimes size={10} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ══ AVISOS DO PROJETO ═══════════════════════════════════════════════════ */}
      {avisosVisivelsProjeto.length > 0 && (
        <div className="listagem-avisos-container">
          {avisosVisivelsProjeto.map((aviso) => {
            const diaLabel = aviso.dia_semana != null ? DIAS_SEMANA[aviso.dia_semana] : null;
            return (
              <div key={aviso.id} className="listagem-aviso-bar listagem-aviso-projeto">
                <span className="listagem-aviso-texto">
                  {diaLabel ? (
                    <>
                      <strong>⚠️ Atenção:</strong> O dia de envio deste projeto é{" "}
                      <strong>{diaLabel}</strong>. {aviso.texto}
                    </>
                  ) : (
                    <>📌 {aviso.texto}</>
                  )}
                </span>
                <button
                  className="listagem-aviso-fechar"
                  onClick={() => setAvisosDescartados((prev) => new Set([...prev, aviso.id]))}
                  aria-label="Fechar aviso"
                  title="Fechar (reaparece na próxima vez que abrir a listagem)"
                >
                  <FaTimes size={10} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* ══ INFO BAR + AÇÕES ════════════════════════════════════════════════════ */}
      <div className="listagem-action-bar">

        {/* Linha 1: avatares */}
        <div className="action-bar-avatars-row">
          {infoGerador && (
            <div className="info-bar-block">
              <AvatarIcon nome={infoGerador.nome} />
              <div className="info-bar-text">
                <span className="info-bar-label">Gerado por</span>
                <span className="info-bar-name">{infoGerador.nome}</span>
              </div>
            </div>
          )}
          {infoEnvio && (
            <div className="info-bar-block">
              <AvatarIcon nome={infoEnvio.nome} />
              <div className="info-bar-text">
                <span className="info-bar-label">Enviado por</span>
                <span className="info-bar-name">{infoEnvio.nome}</span>
              </div>
            </div>
          )}
          {infoEnvio?.data && (
            <div className="info-bar-block">
              <AvatarIcon variant="date" />
              <div className="info-bar-text">
                <span className="info-bar-label">Em</span>
                <span className="info-bar-name">{formatarData(infoEnvio.data)}</span>
              </div>
            </div>
          )}
          {infoRespondido && (
            <div className="info-bar-block">
              <AvatarIcon variant="company" />
              <div className="info-bar-text">
                <span className="info-bar-label">Respondido por</span>
                <span className="info-bar-name">{infoRespondido.nome}</span>
              </div>
            </div>
          )}
        </div>

        {/* Linha 2: botões */}
        {!listagemEnviada && (
          <div className="action-bar-btns-row">

            <input
              type="number"
              min="1"
              max="100"
              value={qtdNovasLinhas}
              onChange={(e) => setQtdNovasLinhas(e.target.value)}
              placeholder="Qtd"
              title="Quantidade de linhas a adicionar"
              className="qtd-linhas-input"
            />
            <button className="add-row-btn action-btn" onClick={addRow}>
              <FaPlus size={11} /> Nova linha
            </button>

            <div className="action-bar-divider" />

            <div className="save-draft-wrapper">
              <button
                className="save-draft-btn action-btn"
                onClick={handleSalvarRascunho}
                disabled={statusSalvar === "salvando"}
                title="Salvar rascunho no banco"
              >
                <FaSave style={{ marginRight: 6 }} />
                {statusSalvar === "salvando" ? "Salvando..." : "Salvar"}
              </button>
              {statusSalvar === "sucesso" && <span className="save-draft-feedback sucesso">✓ Salvo!</span>}
              {statusSalvar === "erro"    && <span className="save-draft-feedback erro">✗ Erro</span>}
            </div>

            {rows.some((r) => r.id) && !modoEdicao && (
              <button
                className="edit-mode-btn action-btn"
                onClick={() => setModoEdicao(true)}
                title="Desbloquear todos os campos para edição"
              >
                <FaEdit style={{ marginRight: 6 }} /> Editar
              </button>
            )}

            <button
              className="atualizar-btn action-btn"
              onClick={handleAtualizar}
              disabled={statusAtualizar === "atualizando"}
              title="Buscar alterações mais recentes do banco"
            >
              <FaSync
                size={12}
                style={{
                  marginRight: 6,
                  animation: statusAtualizar === "atualizando" ? "spin 0.7s linear infinite" : "none",
                }}
              />
              {statusAtualizar === "atualizando" ? "Atualizando..." : statusAtualizar === "sucesso" ? "Atualizado" : "Atualizar"}
            </button>

            <div className="action-bar-divider" />

            <select
              value={setorSelecionado}
              onChange={handleSetorChange}
              className="direcionar-para-input action-btn"
            >
              <option value="">Selecione um setor</option>
              {setoresContainer.map((setor) => (
                <option key={setor.id} value={setor.id}>
                  {setor.nickname || setor.name}
                </option>
              ))}
            </select>

            <div className="send-action-wrapper">
              <button
                className="send-btn action-btn"
                onClick={handleSave}
                disabled={statusEnvio === "enviando" || !setorSelecionado}
              >
                <FaPaperPlane style={{ marginRight: 6 }} /> Enviar
              </button>
              {statusEnvio === "enviando" && <span className="loader-inline" />}
              {statusEnvio === "sucesso"  && <Check />}
            </div>

            <button
              className="export-pdf-btn action-btn"
              onClick={handleExportarPDF}
              title="Exportar listagem como PDF"
            >
              <FaFilePdf style={{ marginRight: 6 }} /> PDF
            </button>

          </div>
        )}

        {listagemEnviada && (
          <div className="action-bar-btns-row">
            <button className="export-pdf-btn action-btn" onClick={handleExportarPDF} title="Exportar listagem como PDF">
              <FaFilePdf style={{ marginRight: 6 }} /> PDF
            </button>
          </div>
        )}

      </div>

      {/* ══ FILTROS ATIVOS ══════════════════════════════════════════════════════ */}
      {temFiltroAtivo && (
        <div className="filtros-ativos-bar">
          {filtros.locacao && (
            <span className="filtro-tag">
              Locação: {filtros.locacao}
              <button onClick={() => setFiltros((f) => ({ ...f, locacao: "" }))}>×</button>
            </span>
          )}
          {filtros.eap && (
            <span className="filtro-tag">
              EAP: {filtros.eap}
              <button onClick={() => setFiltros((f) => ({ ...f, eap: "" }))}>×</button>
            </span>
          )}
          <button
            className="filtro-limpar-todos"
            onClick={() => setFiltros({ locacao: "", eap: "" })}
          >
            Limpar filtros
          </button>
        </div>
      )}

      {/* ══ TABELA ══════════════════════════════════════════════════════════════ */}
      <div className="listagem-table-wrapper">
        <table className="listagem-table">
          <thead>
            <tr>
              <th style={{ width: "30px" }}></th>
              <th style={{ width: "24px" }}></th>{/* coluna histórico */}
              <th>#</th>
              <th>Código</th>
              <th>Descrição</th>
              <th>Unidade</th>
              <th className="quantidade-pavimento-header">Qnt/pav</th>
              <th className="th-filtro">
                <span>Locação</span>
                <button
                  className={`filtro-icone-btn ${filtros.locacao ? "filtro-ativo" : ""}`}
                  onClick={() => setFiltroAbertoCol(filtroAbertoCol === "locacao" ? null : "locacao")}
                  title="Filtrar locação"
                >
                  <FaFilter size={10} />
                </button>
                {filtroAbertoCol === "locacao" && (
                  <div ref={filtroRef} className="filtro-dropdown">
                    <div
                      className={`filtro-option ${filtros.locacao === "" ? "filtro-option-selected" : ""}`}
                      onClick={() => { setFiltros((f) => ({ ...f, locacao: "" })); setFiltroAbertoCol(null); }}
                    >Todos</div>
                    {valoresUnicos("locacao").map((v) => (
                      <div
                        key={v}
                        className={`filtro-option ${filtros.locacao === v ? "filtro-option-selected" : ""}`}
                        onClick={() => { setFiltros((f) => ({ ...f, locacao: v })); setFiltroAbertoCol(null); }}
                      >{v}</div>
                    ))}
                  </div>
                )}
              </th>
              <th className="th-filtro">
                <span>EAP</span>
                <button
                  className={`filtro-icone-btn ${filtros.eap ? "filtro-ativo" : ""}`}
                  onClick={() => setFiltroAbertoCol(filtroAbertoCol === "eap" ? null : "eap")}
                  title="Filtrar EAP"
                >
                  <FaFilter size={10} />
                </button>
                {filtroAbertoCol === "eap" && (
                  <div ref={filtroRef} className="filtro-dropdown">
                    <div
                      className={`filtro-option ${filtros.eap === "" ? "filtro-option-selected" : ""}`}
                      onClick={() => { setFiltros((f) => ({ ...f, eap: "" })); setFiltroAbertoCol(null); }}
                    >Todos</div>
                    {valoresUnicos("eap").map((v) => (
                      <div
                        key={v}
                        className={`filtro-option ${filtros.eap === v ? "filtro-option-selected" : ""}`}
                        onClick={() => { setFiltros((f) => ({ ...f, eap: v })); setFiltroAbertoCol(null); }}
                      >{v}</div>
                    ))}
                  </div>
                )}
              </th>
              <th>Observação</th>
              <th>Comentário</th>
              {!listagemEnviada && <th style={{ width: "40px" }}>Ações</th>}
            </tr>
          </thead>

          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="listagem-itens">
              {(provided) => (
                <tbody ref={provided.innerRef} {...provided.droppableProps}>
                  {rowsParaExibir.map((row, visualIdx) => {
                    const isCriar = row.codigo?.toLowerCase() === "criar";
                    const foiEnviada = listagemEnviada;
                    const isLinhaCongelada = !modoEdicao && (foiEnviada || !!row.id);
                    const podeEditarCodigo    = modoEdicao || (!row.id && !foiEnviada);
                    const podeEditarDescricao = (modoEdicao && isCriar) || (isCriar && !foiEnviada);
                    const podeEditarDemais    = modoEdicao || (!row.id && !foiEnviada);
                    const uid = row._uid;
                    const temCodigo = row.codigo?.trim() && row.codigo.toLowerCase() !== "criar";
                    
                    const isDraggable = !foiEnviada && podeEditarDemais && row.id;

                    return (
                      <Draggable 
                        key={uid} 
                        draggableId={String(uid)} 
                        index={visualIdx}
                        isDragDisabled={!isDraggable}
                      >
                        {(draggableProvided, snapshot) => (
                          <tr
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            className={`${isLinhaCongelada ? "linha-congelada" : ""} ${snapshot.isDragging ? "item-dragging" : ""}`}
                            style={{
                              ...draggableProvided.draggableProps.style,
                              opacity: snapshot.isDragging ? 0.85 : 1,
                              backgroundColor: snapshot.isDragging ? "#f1f5f9" : undefined,
                            }}
                          >
                            {/* Drag handle */}
                            <td 
                              {...(isDraggable ? draggableProvided.dragHandleProps : {})} 
                              className="drag-handle-cell"
                              style={{ cursor: isDraggable ? "grab" : "default", width: "30px", textAlign: "center" }}
                              title={isDraggable ? "Arrastar para reordenar" : ""}
                            >
                              {isDraggable && (
                                <span style={{ color: "#94a3b8", fontSize: "16px" }}>⋮⋮</span>
                              )}
                            </td>

                            {/* ── Botão histórico ── */}
                            <td style={{ position: "relative", overflow: "visible", width: "24px", padding: "0 2px" }}>
                              {temCodigo && (
                                <>
                                  <button
                                    className="historico-btn"
                                    title={`Histórico de pedidos — ${row.codigo}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setHistoricoAbertoUid(historicoAbertoUid === uid ? null : uid);
                                    }}
                                  >
                                    <FaHistory size={11} />
                                  </button>
                                  {historicoAbertoUid === uid && (
                                    <HistoricoPopover
                                      codigo={row.codigo}
                                      projetoId={projetoAtual?.id}
                                      notaAtualId={notaAtual?.id}
                                      onClose={() => setHistoricoAbertoUid(null)}
                                    />
                                  )}
                                </>
                              )}
                            </td>

                            <td>{rowsParaExibir.length - visualIdx}</td>
                            <td>
                              {podeEditarCodigo ? (
                                <div className="codigo-com-lupa">
                                  <input
                                    type="text"
                                    value={row.codigo}
                                    onChange={(e) => {
                                      handleInputChange(uid, "codigo", e.target.value);
                                      if (codigoErro.has(uid)) {
                                        setCodigoErro((prev) => { const novo = new Set(prev); novo.delete(uid); return novo; });
                                      }
                                    }}
                                    onBlur={() => !isCriar && buscarItemPorCodigo(uid, row.codigo)}
                                    onKeyPress={(e) => !isCriar && handleCodigoEnter(e, uid, row.codigo)}
                                    placeholder="Código"
                                    className={codigoErro.has(uid) ? "codigo-invalido" : ""}
                                  />
                                  <button
                                    type="button"
                                    className="lupa-busca-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setLinhaBuscaAtiva(uid);
                                      setBuscaInsumoAberta(true);
                                    }}
                                    title="Buscar insumo"
                                  >
                                    <FaMagnifyingGlass />
                                  </button>
                                </div>
                              ) : (
                                <span>{row.codigo || ""}</span>
                              )}
                            </td>
                            <td>
                              {podeEditarDescricao ? (
                                <input
                                  className="descri"
                                  type="text"
                                  value={row.descricao || ""}
                                  onChange={(e) => handleInputChange(uid, "descricao", e.target.value)}
                                  placeholder="Descrição do novo item"
                                />
                              ) : (
                                <span>{row.descricao || ""}</span>
                              )}
                            </td>
                            <td>
                              {podeEditarDemais && isCriar ? (
                                <select
                                  value={row.unidade || ""}
                                  onChange={(e) => handleInputChange(uid, "unidade", e.target.value)}
                                >
                                  <option value=""></option>
                                  {unidadesDisponiveis.map((un, i) => (
                                    <option key={i} value={un}>{un}</option>
                                  ))}
                                </select>
                              ) : (
                                <span>{row.unidade || ""}</span>
                              )}
                            </td>
                            <td>
                              {podeEditarDemais ? (
                                <input
                                  type="number"
                                  value={row.quantidade ?? ""}
                                  onChange={(e) => handleInputChange(uid, "quantidade", e.target.value)}
                                  min="0"
                                  step="any"
                                />
                              ) : (
                                <span>{row.quantidade || ""}</span>
                              )}
                            </td>
                            <td>
                              {podeEditarDemais ? (
                                <>
                                  <div
                                    className="locacao-trigger"
                                    onClick={() => setDropdownAberto(uid)}
                                  >
                                    {row.locacao?.length === 0
                                      ? "Selecionar"
                                      : row.locacao.length === 1
                                      ? row.locacao[0]
                                      : `${row.locacao[0]} +`}
                                  </div>
                                  {dropdownAberto === uid && (
                                    <div ref={dropdownRef} className="locacao-dropdown">
                                      {locacoes.map((loc) => (
                                        <label key={loc} className="locacao-option">
                                          <input
                                            type="checkbox"
                                            checked={row.locacao?.includes(loc) || false}
                                            onChange={(e) => handleLocacaoChange(uid, loc, e.target.checked)}
                                          />
                                          <span>{loc}</span>
                                        </label>
                                      ))}
                                    </div>
                                  )}
                                </>
                              ) : (
                                <>
                                  <div
                                    className={`locacao-resumo ${row.locacao?.length > 1 ? "locacao-resumo-clickable" : ""}`}
                                    onClick={() => { if (row.locacao?.length > 1) setTooltipVisualizacao(uid); }}
                                  >
                                    {row.locacao?.length === 0
                                      ? "–"
                                      : row.locacao.length === 1
                                      ? row.locacao[0]
                                      : `${row.locacao[0]} +`}
                                  </div>
                                  {tooltipVisualizacao === uid && (
                                    <div ref={tooltipRef} className="locacao-tooltip">
                                      {row.locacao.map((loc, i) => (
                                        <div key={i} className="locacao-tooltip-item">• {loc}</div>
                                      ))}
                                    </div>
                                  )}
                                </>
                              )}
                            </td>
                            <td>
                              {podeEditarDemais ? (
                                <select
                                  value={row.eap || ""}
                                  onChange={(e) => handleInputChange(uid, "eap", e.target.value)}
                                >
                                  <option value=""></option>
                                  {eaps.map((eap, i) => (
                                    <option key={i} value={eap}>{eap}</option>
                                  ))}
                                </select>
                              ) : (
                                <span>{row.eap || ""}</span>
                              )}
                            </td>
                            <td>
                              {podeEditarDemais ? (
                                <textarea
                                  value={row.observacao || ""}
                                  onChange={(e) => handleInputChange(uid, "observacao", e.target.value)}
                                  onBlur={(e) => handleObservacaoBlur(uid, e.target.value)}
                                  className="observacao-textarea"
                                  rows="1"
                                />
                              ) : (
                                <div className="observacao-rendered">{row.observacao || ""}</div>
                              )}
                            </td>
                            <td>
                              <div className="observacao-rendered">{row.comentario || ""}</div>
                            </td>
                            {!listagemEnviada && (
                              <td>
                                <div className="button-group">
                                  {!foiEnviada && (
                                    <FaTrash
                                      className="delete-icon"
                                      onClick={(e) => { e.stopPropagation(); removeRow(uid); }}
                                    />
                                  )}
                                </div>
                              </td>
                            )}
                          </tr>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </tbody>
              )}
            </Droppable>
          </DragDropContext>
        </table>
      </div>

      {/* Modal de Busca de Insumo */}
      <BuscaInsumo
        isOpen={buscaInsumoAberta}
        onClose={() => { setBuscaInsumoAberta(false); setLinhaBuscaAtiva(null); }}
        onSelect={(codigo) => {
          if (linhaBuscaAtiva !== null) {
            handleInputChange(linhaBuscaAtiva, "codigo", codigo);
            buscarItemPorCodigo(linhaBuscaAtiva, codigo);
          }
          setBuscaInsumoAberta(false);
          setLinhaBuscaAtiva(null);
        }}
      />
    </div>
  );
}